import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Users as UsersIcon, Plus, Pencil, KeyRound, Trash2, X, ShieldCheck, ShieldAlert } from "lucide-react";

const ROLE_LABEL = { master: "Master", operacional: "Operacional" };

export default function Users() {
    const { user } = useAuth();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [modal, setModal] = useState(null); // null | { mode: "create"|"edit"|"reset", target? }

    async function refresh() {
        setLoading(true);
        try {
            const { data } = await api.get("/users");
            setList(data || []);
        } catch (e) {
            setError(formatApiErrorDetail(e?.response?.data?.detail) || "Falha ao listar usuários");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { refresh(); }, []);

    const sorted = useMemo(() => [...list].sort((a, b) => (a.name || "").localeCompare(b.name || "")), [list]);

    if (user && user.role !== "master" && user.role !== "admin") {
        return <Navigate to="/" replace />;
    }

    return (
        <div className="space-y-6 fade-in" data-testid="users-page">
            <header className="flex items-center justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-medium">Gestão de acesso</div>
                    <h1 className="font-display font-bold text-3xl text-[#0F2044] mt-1">Usuários</h1>
                    <p className="text-sm text-slate-500 mt-1">Crie e edite usuários do console. Apenas usuários Master têm acesso a esta página.</p>
                </div>
                <button onClick={() => setModal({ mode: "create" })} data-testid="new-user-button"
                    className="inline-flex items-center gap-2 bg-[#0F2044] hover:bg-[#172E5E] text-white text-sm font-semibold px-4 py-2.5 rounded-md transition-colors">
                    <Plus className="h-4 w-4" /> Novo usuário
                </button>
            </header>

            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="users-table-card">
                {loading ? (
                    <div className="p-6 text-sm text-slate-500">Carregando…</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr className="text-left text-xs uppercase tracking-[0.12em] text-slate-500 border-b border-slate-200">
                                <th className="py-3 pl-4 pr-3 font-medium">Nome</th>
                                <th className="py-3 pr-3 font-medium">E-mail</th>
                                <th className="py-3 pr-3 font-medium">Perfil</th>
                                <th className="py-3 pr-3 font-medium">Status</th>
                                <th className="py-3 pr-4 font-medium text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.length === 0 && (
                                <tr><td colSpan={5} className="text-center text-slate-500 py-10">Nenhum usuário ainda.</td></tr>
                            )}
                            {sorted.map((u) => (
                                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                                    <td className="pl-4 pr-3 py-3 font-medium text-[#0F2044]">{u.name || "—"}</td>
                                    <td className="pr-3 py-3 text-slate-700">{u.email}</td>
                                    <td className="pr-3 py-3">
                                        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full ${u.role === "master" ? "bg-indigo-50 text-indigo-700 border border-indigo-200" : "bg-slate-100 text-slate-700 border border-slate-200"}`}>
                                            {u.role === "master" ? <ShieldCheck className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />}
                                            {ROLE_LABEL[u.role] || u.role}
                                        </span>
                                    </td>
                                    <td className="pr-3 py-3">
                                        <span className={`text-xs font-semibold ${u.active === false ? "text-red-700" : "text-emerald-700"}`}>
                                            {u.active === false ? "Desativado" : "Ativo"}
                                        </span>
                                        {u.must_change_password && <span className="ml-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">trocar senha</span>}
                                    </td>
                                    <td className="pr-4 py-3 text-right">
                                        <div className="inline-flex items-center gap-1">
                                            <button onClick={() => setModal({ mode: "edit", target: u })} data-testid={`edit-user-${u.id}`}
                                                className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-white text-slate-700 inline-flex items-center gap-1">
                                                <Pencil className="h-3 w-3" /> Editar
                                            </button>
                                            <button onClick={() => setModal({ mode: "reset", target: u })} data-testid={`reset-user-${u.id}`}
                                                className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-white text-slate-700 inline-flex items-center gap-1">
                                                <KeyRound className="h-3 w-3" /> Senha
                                            </button>
                                            {u.id !== user?.id && (
                                                <button onClick={async () => {
                                                    if (!confirm(`Excluir ${u.name || u.email}?`)) return;
                                                    try { await api.delete(`/users/${u.id}`); refresh(); }
                                                    catch (e) { alert(formatApiErrorDetail(e?.response?.data?.detail) || "Erro"); }
                                                }} data-testid={`delete-user-${u.id}`}
                                                    className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-1">
                                                    <Trash2 className="h-3 w-3" /> Excluir
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {modal && (
                <UserModal
                    mode={modal.mode}
                    target={modal.target}
                    onClose={() => setModal(null)}
                    onSaved={() => { setModal(null); refresh(); }}
                />
            )}
        </div>
    );
}

function UserModal({ mode, target, onClose, onSaved }) {
    const isCreate = mode === "create";
    const isReset = mode === "reset";
    const [name, setName] = useState(target?.name || "");
    const [email, setEmail] = useState(target?.email || "");
    const [role, setRole] = useState(target?.role || "operacional");
    const [password, setPassword] = useState("");
    const [active, setActive] = useState(target?.active !== false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    async function submit(e) {
        e.preventDefault();
        setError(""); setSaving(true);
        try {
            if (isCreate) {
                if (!name.trim() || !email.trim() || !password.trim()) {
                    setError("Preencha nome, e-mail e senha."); setSaving(false); return;
                }
                await api.post("/users", { name, email, password, role });
            } else if (isReset) {
                if (!password.trim()) { setError("Informe a nova senha."); setSaving(false); return; }
                await api.patch(`/users/${target.id}`, { password });
            } else {
                await api.patch(`/users/${target.id}`, { name, role, active });
            }
            onSaved();
        } catch (e2) {
            setError(formatApiErrorDetail(e2?.response?.data?.detail) || "Falha");
        } finally {
            setSaving(false);
        }
    }

    const titles = {
        create: "Novo usuário",
        edit: `Editar usuário — ${target?.email}`,
        reset: `Redefinir senha — ${target?.email}`,
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="user-modal">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
                    <h3 className="font-display font-semibold text-[#0F2044]">{titles[mode]}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="user-modal-close"><X className="h-5 w-5" /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-3">
                    {(isCreate || mode === "edit") && (
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Nome</label>
                            <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="user-name-input" className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm" />
                        </div>
                    )}
                    {isCreate && (
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">E-mail</label>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="user-email-input" className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm" />
                        </div>
                    )}
                    {(isCreate || mode === "edit") && (
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Perfil</label>
                            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="user-role-input" className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white">
                                <option value="operacional">Operacional</option>
                                <option value="master">Master</option>
                            </select>
                        </div>
                    )}
                    {(isCreate || isReset) && (
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">{isCreate ? "Senha inicial" : "Nova senha"}</label>
                            <input value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="user-password-input" className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm" />
                            <p className="text-[11px] text-slate-500 mt-1">O usuário será obrigado a trocar a senha no próximo login.</p>
                        </div>
                    )}
                    {mode === "edit" && (
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="user-active-input" />
                            Usuário ativo
                        </label>
                    )}
                    {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="text-sm px-4 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">Cancelar</button>
                        <button type="submit" disabled={saving} data-testid="user-modal-submit" className="text-sm px-4 py-2 rounded-md bg-[#0F2044] hover:bg-[#172E5E] text-white font-semibold disabled:opacity-60">
                            {saving ? "Salvando…" : "Salvar"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
